import { Table, StatusPill } from '../../../design-system';
import { useReport, ReportCard, StatRow, Stat, Donut, Gauge, StackedBars, fmtPct, fmtNum } from './reportsKit';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

const STATUS_SERIES = [
  { key: 'filed', label: 'Filed', color: 'var(--ds-success-600)' },
  { key: 'late', label: 'Late', color: 'var(--ds-warning-600)' },
  { key: 'overdue', label: 'Overdue', color: 'var(--ds-danger-600)' },
  { key: 'pending', label: 'Pending', color: 'var(--ds-neutral-400)' },
];

function cellDot(c) {
  if (!c || !c.total) return <span className="rep-dot rep-dot-slate" title="none" />;
  const tone = c.overdue ? 'red' : c.late || c.pending ? 'amber' : 'green';
  return (
    <span className="rep-matrix-cell" title={`filed ${c.filed} · late ${c.late} · pending ${c.pending} · overdue ${c.overdue}`}>
      <span className={`rep-dot rep-dot-${tone}`} />
      {c.total}
    </span>
  );
}

export default function ComplianceReports({ range, types = [] }) {
  // Selected compliance-head ids → a comma-joined param (undefined = all heads),
  // so the head-based cards filter to the picked types.
  const typeParam = types.length ? types.join(',') : undefined;
  const otf = useReport('on-time-filing', { ...range, types: typeParam });
  const matrix = useReport('compliance-matrix', { types: typeParam });
  const overdue = useReport('overdue-register', { types: typeParam });
  const gstin = useReport('gstin-health');

  const o = otf.data?.overall;
  const matrixCols = [
    { key: 'name', header: 'Client', render: (r) => <strong>{r.name}</strong> },
    ...(matrix.data?.heads || []).map((h) => ({ key: h.code, header: h.name, render: (r) => cellDot(r.cells[h.code]) })),
  ];

  const g = gstin.data;
  const gstinDonut = [
    { label: 'Active', value: g?.byStatus?.Active || 0, color: 'var(--ds-success-600)' },
    { label: 'Non-filing', value: g?.byStatus?.Non_Filing || 0, color: 'var(--ds-danger-600)' },
    { label: 'Suspended', value: g?.byStatus?.Suspended || 0, color: 'var(--ds-warning-600)' },
    { label: 'Cancelled', value: g?.byStatus?.Cancelled || 0, color: 'var(--ds-neutral-400)' },
  ];

  return (
    <div className="rep-stack">
      {/* A1 — On-Time Filing */}
      <ReportCard icon="📋" title="On-Time Filing" subtitle="Statutory compliances filed on/before their due date" loading={otf.loading} error={otf.error}>
        <div className="rep-grid-3">
          <Gauge value={o?.onTimePct == null ? null : o.onTimePct} label="on-time" />
          <div>
            <StatRow>
              <Stat label="Filed" value={fmtNum(o?.filed)} tone="green" />
              <Stat label="Late" value={fmtNum(o?.late)} tone="amber" />
              <Stat label="Overdue" value={fmtNum(o?.overdue)} tone="red" />
              <Stat label="Pending" value={fmtNum(o?.pending)} tone="slate" />
            </StatRow>
            <StackedBars data={otf.data?.byMonth || []} series={STATUS_SERIES} height={130} emptyText="No compliance periods in range" />
          </div>
        </div>
        <p className="rep-note">By head</p>
        <Table
          keyField="key"
          columns={[
            { key: 'key', header: 'Head', render: (r) => <strong>{r.key}</strong> },
            { key: 'filed', header: 'Filed' },
            { key: 'late', header: 'Late' },
            { key: 'overdue', header: 'Overdue' },
            { key: 'pending', header: 'Pending' },
            { key: 'onTimePct', header: 'On-time', render: (r) => <StatusPill variant={r.onTimePct == null ? 'neutral' : r.onTimePct >= 90 ? 'success' : r.onTimePct >= 70 ? 'warning' : 'danger'} label={fmtPct(r.onTimePct)} /> },
          ]}
          data={otf.data?.byHead || []}
        />
      </ReportCard>

      {/* A2 — Compliance Status Matrix */}
      <ReportCard icon="🗂️" title="Compliance Status Matrix" subtitle="Clients × heads — dot = worst open status, number = periods" loading={matrix.loading} error={matrix.error}>
        {(matrix.data?.clients || []).length === 0 ? (
          <p className="rep-empty">No compliance instances yet.</p>
        ) : (
          <Table keyField="id" columns={matrixCols} data={matrix.data.clients} />
        )}
        <p className="rep-note-plain">
          <span className="rep-dot rep-dot-green" /> filed &nbsp; <span className="rep-dot rep-dot-amber" /> pending / late &nbsp; <span className="rep-dot rep-dot-red" /> overdue
        </p>
      </ReportCard>

      {/* A3 — Overdue & At-Risk Register */}
      <ReportCard icon="⏰" title="Overdue & At-Risk Register" subtitle={`Past due, or due within ${overdue.data?.soonDays ?? 14} days`} loading={overdue.loading} error={overdue.error}>
        <StatRow>
          <Stat label="Overdue" value={fmtNum(overdue.data?.overdueCount)} tone="red" />
          <Stat label="At-risk, no task" value={fmtNum(overdue.data?.atRiskNoTaskCount)} tone="amber" />
          <Stat label="Total flagged" value={fmtNum(overdue.data?.rows?.length)} tone="slate" />
        </StatRow>
        <Table
          keyField="id"
          columns={[
            { key: 'client', header: 'Client', render: (r) => <strong>{r.client}</strong> },
            { key: 'head', header: 'Head' },
            { key: 'period', header: 'Period' },
            { key: 'dueDate', header: 'Due', render: (r) => fmtDate(r.dueDate) },
            { key: 'daysToDue', header: 'Status', render: (r) => (r.overdue ? <StatusPill variant="danger" label={`${Math.abs(r.daysToDue)}d overdue`} /> : <StatusPill variant="warning" label={`in ${r.daysToDue}d`} />) },
            { key: 'hasTask', header: 'Task', render: (r) => (r.hasTask ? <StatusPill variant="success" label="raised" /> : <StatusPill variant="neutral" label="none" />) },
          ]}
          data={overdue.data?.rows || []}
        />
      </ReportCard>

      {/* A4 — GSTIN Filing Health */}
      <ReportCard icon="🧾" title="GSTIN Filing Health" subtitle="Registration status and filing frequency across GSTINs" loading={gstin.loading} error={gstin.error}>
        <div className="rep-grid-3">
          <Donut segments={gstinDonut} centerValue={fmtNum(g?.total)} centerLabel="GSTINs" />
          <div>
            <StatRow>
              <Stat label="Active" value={fmtNum(g?.byStatus?.Active)} tone="green" />
              <Stat label="Monthly filers" value={fmtNum(g?.byFrequency?.Monthly)} tone="blue" />
              <Stat label="Quarterly (QRMP)" value={fmtNum(g?.byFrequency?.Quarterly)} tone="slate" />
              <Stat label="Issues" value={fmtNum(g?.problems?.length)} tone="red" />
            </StatRow>
            {(g?.problems || []).length > 0 && (
              <Table
                keyField="gstin"
                columns={[
                  { key: 'client', header: 'Client' },
                  { key: 'gstin', header: 'GSTIN' },
                  { key: 'status', header: 'Status', render: (r) => <StatusPill variant="danger" label={r.status || 'unknown'} /> },
                  { key: 'filingFrequency', header: 'Freq' },
                ]}
                data={g.problems}
              />
            )}
          </div>
        </div>
      </ReportCard>
    </div>
  );
}
